// components/media/SelectProjOrBlog.js
import React, { useEffect } from 'react';
import {
  Modal,
  List,
  Avatar,
  Badge,
  Button,
  Space,
  Typography
} from 'antd';
import {
  AppstoreOutlined,
  FolderOutlined,
  EditOutlined,
  FileOutlined,
  StarOutlined,
  PlusOutlined
} from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import { getFeaturedImage } from '../../features/media/mediaSlice';

const { Text } = Typography;

const SelectProjOrBlog = ({ visible, onCancel, onEntitySelect }) => {
  const dispatch = useDispatch();
  const { desprojects } = useSelector((state) => state.desprojects);
  const { devprojects } = useSelector((state) => state.devprojects);
  const { blogs } = useSelector((state) => state.blogs);
  const { featuredImages } = useSelector((state) => state.media);

  // Load featured images for all entities when modal opens
  useEffect(() => {
    if (visible) {
      // Load featured images for all entities
      const allEntities = getAllEntities();
      allEntities.forEach(entity => {
        dispatch(getFeaturedImage({
          entityType: entity.type,
          entityId: entity.id
        }));
      });
    }
  }, [visible, dispatch]);

  // Get all entities for selection
  const getAllEntities = () => {
    const entities = [];
        
    if (desprojects?.length) {
      desprojects.forEach(project => {
        entities.push({
          id: project._id,
          name: project.name,
          type: 'desproject',
          coverImage: project.url,
          description: project.desc,
          status: project.status,
          createdAt: project.createdAt
        });
      });
    }
        
    if (devprojects?.length) {
      devprojects.forEach(project => {
        entities.push({
          id: project._id,
          name: project.name,
          type: 'devproject',
          coverImage: project.url,
          description: project.desc,
          status: project.status,
          createdAt: project.createdAt
        });
      });
    }
        
    if (blogs?.length) {
      blogs.forEach(blog => {
        entities.push({
          id: blog._id,
          name: blog.title,
          type: 'blog',
          coverImage: blog.featuredImage?.url,
          description: blog.summary,
          status: blog.status,
          createdAt: blog.createdAt
        });
      });
    }
        
    return entities.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  };

  // Get featured image for an entity
  const getFeaturedImageForEntity = (entity) => {
    const key = `${entity.type}_${entity.id}`;
    const featuredImage = featuredImages[key];
    
    // If we have it from Redux, use that
    if (featuredImage) {
      return featuredImage.url;
    }
    
    // Fallback to entity's direct image
    return entity.coverImage;
  };

  const getEntityTypeIcon = (type) => {
    switch (type) {
      case 'desproject':
        return <AppstoreOutlined style={{ color: '#1890ff' }} />;
      case 'devproject':
        return <FolderOutlined style={{ color: '#52c41a' }} />;
      case 'blog':
        return <EditOutlined style={{ color: '#fa8c16' }} />;
      default:
        return <FileOutlined />;
    }
  };

  const getEntityTypeBadgeColor = (type) => {
    switch (type) {
      case 'desproject':
        return '#1890ff';
      case 'devproject':
        return '#52c41a';
      case 'blog':
        return '#fa8c16';
      default:
        return '#999';
    }
  };

  const getStatusBadge = (status, type) => {
    if (type === 'blog') {
      const statusColors = {
        'draft': '#faad14',
        'published': '#52c41a',
        'archived': '#999'
      };
      return (
        <Badge 
          color={statusColors[status] || '#999'} 
          text={status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown'} 
        />
      );
    } else {
      // For projects, status is boolean
      return (
        <Badge 
          color={status ? '#52c41a' : '#faad14'} 
          text={status ? 'Active' : 'Inactive'} 
        />
      );
    }
  };

  const handleEntitySelect = (entity) => {
    // Add featured image info to entity data
    const entityWithImage = {
      ...entity,
      featuredImage: getFeaturedImageForEntity(entity) ? {
        url: getFeaturedImageForEntity(entity)
      } : null
    };
    onEntitySelect(entityWithImage);
  };

  const formatEntityType = (type) => {
    switch (type) {
      case 'desproject':
        return 'Design Project';
      case 'devproject':
        return 'Development Project';
      case 'blog':
        return 'Blog Post';
      default:
        return type;
    }
  };

  const allEntities = getAllEntities();

  return (
    <Modal
      title="Select Project or Blog Post"
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width={900}
      bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
    >
      {/* Header with count - moved inside modal body to avoid overlap */}
      <div className="mb-4 pb-3 border-b border-gray-200">
        <Text type="secondary" className="text-sm">
          {allEntities.length} {allEntities.length === 1 ? 'item' : 'items'} available
        </Text>
      </div>

      {allEntities.length === 0 ? (
        <div className="text-center py-12">
          <FileOutlined className="text-6xl text-gray-400 mb-4" />
          <Text className="text-gray-500 text-lg block mb-2">No Projects or Blog Posts Found</Text>
          <Text className="text-gray-400">
            Create some projects or blog posts first to manage their media
          </Text>
        </div>
      ) : (
        <List
          dataSource={allEntities}
          renderItem={(entity) => {
            const featuredImageUrl = getFeaturedImageForEntity(entity);
            return (
              <List.Item
                key={`${entity.type}_${entity.id}`}
                onClick={() => handleEntitySelect(entity)}
                style={{ cursor: 'pointer' }}
                className="hover:bg-gray-50 rounded p-3 transition-colors"
                actions={[
                  <Button
                    key="select"
                    type="primary"
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEntitySelect(entity);
                    }}
                  >
                    Select
                  </Button>
                ]}
              >
                <List.Item.Meta
                  avatar={
                    <div style={{ position: 'relative' }}>
                      {featuredImageUrl ? (
                        <Avatar 
                          size={64} 
                          src={featuredImageUrl} 
                          shape="square" 
                          style={{ borderRadius: '8px' }}
                        />
                      ) : (
                        <Avatar 
                          size={64} 
                          icon={getEntityTypeIcon(entity.type)} 
                          shape="square"
                          style={{ 
                            borderRadius: '8px',
                            backgroundColor: '#f5f5f5',
                            color: getEntityTypeBadgeColor(entity.type)
                          }}
                        />
                      )}
                      {featuredImageUrl && (
                        <StarOutlined 
                          style={{ 
                            position: 'absolute', 
                            top: 2, 
                            right: 2, 
                            color: '#faad14',
                            background: 'white',
                            borderRadius: '50%',
                            padding: '2px',
                            fontSize: '10px'
                          }} 
                          title={entity.type === 'blog' ? 'Featured Image' : 'Cover Image'}
                        />
                      )}
                    </div>
                  }
                  title={
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Text strong className="text-lg">{entity.name}</Text>
                        <Badge 
                          color={getEntityTypeBadgeColor(entity.type)} 
                          text={formatEntityType(entity.type)} 
                        />
                      </div>
                      {getStatusBadge(entity.status, entity.type)}
                    </div>
                  }
                  description={
                    <div>
                      <Space size="small">
                        <Text type="secondary" className="text-xs">
                          Created: {new Date(entity.createdAt).toLocaleDateString()}
                        </Text>
                        {featuredImageUrl && (
                          <>
                            <Text type="secondary">•</Text>
                            <Text type="secondary" className="text-xs">
                              <StarOutlined style={{ marginRight: 4 }} />
                              Has {entity.type === 'blog' ? 'featured' : 'cover'} image
                            </Text>
                          </>
                        )}
                      </Space>
                    </div>
                  }
                />
              </List.Item>
            );
          }}
        />
      )}
    </Modal>
  );
};

export default SelectProjOrBlog;